import React, { useContext, useState } from 'react'
import { AutoColumn } from '../../components/Column'
import styled, { ThemeContext } from 'styled-components'
import { TYPE } from '../../theme'
import Card from '../../components/Card'
import { ButtonError } from '../../components/Button'
import { useIsTransactionPending } from '../../state/transactions/hooks'
import { useCheckUserHasAvailableClaim, useClaimCallback, useUserUnclaimedAmount } from '../../state/airdrop/hooks'
import { useActiveWeb3React } from '../../hooks'
import Confetti from '../../components/Confetti'

const BodyWrapper = styled.div`
  position: relative;
  max-width: 550px;
  width: 100%;
  background: ${({ theme }) => theme.bg1};
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);
  padding: 2.5rem;
`

const PageWrapper = styled(AutoColumn)``

const TopSection = styled(AutoColumn)`
  max-width: 640px;
  width: 100%;
`

const EmptyProposals = styled.div`
  border: 1px solid ${({ theme }) => theme.text4};
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
`

export const Dots = styled.span`
  &::after {
    display: inline-block;
    animation: ellipsis 1.25s infinite;
    content: '.';
    width: 1em;
    text-align: left;
  }

  @keyframes ellipsis {
    0% {
      content: '.';
    }
    33% {
      content: '..';
    }
    66% {
      content: '...';
    }
  }
`

export default function Vote() {
  const { account } = useActiveWeb3React()

  const theme = useContext(ThemeContext)

  // used for UI loading states
  const [attempting, setAttempting] = useState<number>(1)
  const [canClaim, setCanClaim] = useState<boolean | undefined>(false)

  // monitor the status of the claim from contracts and txns
  const { claimCallback } = useClaimCallback(account)

  const claimAmount = useUserUnclaimedAmount(account)

  useCheckUserHasAvailableClaim(account).then(result => {
    if (result !== undefined) {
      setAttempting(0)
    }
    setCanClaim(result)
  })

  const [hash, setHash] = useState<string | undefined>()

  // monitor the status of the claim from contracts and txns
  const claimPending = useIsTransactionPending(hash ?? '')
  const claimConfirmed = hash && !claimPending

  const [error, setError] = useState<any | undefined>()

  // use the hash to monitor this txn

  function onClaim() {
    setAttempting(2)
    setError(null)
    claimCallback()
      .then(hash => {
        setAttempting(0)
        setHash(hash)
      })
      .catch(err => {
        setAttempting(0)
        setError(err)
      })
  }

  function getErrorMessage(): string | null {
    let result = null

    if (error && error['data'] && error['data']['message']) {
      result = error['data']['message'] + ' Tray again later'
    }

    return result
  }

  return (
    <BodyWrapper>
      <Confetti start={Boolean(claimConfirmed)} />
      <PageWrapper gap="lg" justify="center">
        <TopSection gap="2px">
          <TYPE.mediumHeader style={{ margin: '.5rem 0' }} textAlign="center">
            Claim YTS from Airdrop
          </TYPE.mediumHeader>
          {!account ? (
            <Card padding="40px">
              <TYPE.body color={theme.text3} textAlign="center">
                Connect to a wallet to view your liquidity.
              </TYPE.body>
            </Card>
          ) : canClaim === false ? (
            <Card padding="40px">
              <TYPE.body color={theme.text3} textAlign="center">
                You have no available claim.
              </TYPE.body>
            </Card>
          ) : attempting ? (
            <EmptyProposals>
              <TYPE.body color={theme.text3} textAlign="center">
                <Dots>{attempting === 1 ? 'Checking' : 'Loading'}</Dots>
              </TYPE.body>
            </EmptyProposals>
          ) : claimConfirmed ? (
            <TYPE.subHeader mt="1rem" fontWeight={500} textAlign="center" color={theme.text1}>
              <span role="img" aria-label="party-hat">
                🎉{' '}
              </span>
              Welcome to team YetiSwap
              <span role="img" aria-label="party-hat">
                {' '}
                🎉
              </span>
            </TYPE.subHeader>
          ) : (
            <ButtonError error={false} padding="16px 16px" width="100%" mt="1rem" onClick={onClaim}>
              {'Claim ' + claimAmount?.toFixed(0, { groupSeparator: ',' }) + ' YTS'}
            </ButtonError>
          )}

          {error && !claimConfirmed && getErrorMessage() && (
            <ButtonError error={true} padding="16px 16px" width="100%" mt="1rem">
              {getErrorMessage()}
            </ButtonError>
          )}
        </TopSection>
      </PageWrapper>
    </BodyWrapper>
  )
}
